import React, { useMemo } from 'react';

import { useAggregateGovernanceStatusByDomainQuery } from '../../graphql/search.generated';
import { colorUtilService, ColumnChart, ColumnChartSeriesItem } from '../shared/charts';
import { FacetFilterInput } from '../../types.generated';
import { useTranslation } from '../../i18n/useTranslation';
import { DOMAINS_FILTER_NAME } from '../search/utils/constants';
import { COMMON_ASSET_ENTITY_TYPES, getAggEntityName, getFacetAggregations } from './utils';
import { ChartWidgetCard } from './ChartWidgetCard';
import { mergeDashboardFilters, useDashboardFilters } from './dashboardFilters';

const TOP_DOMAINS_LIMIT = 10;

const GOVERNANCE_STATUS_FILTER = 'governanceStatus';
const GOVERNANCE_STATUS_DRAFT = 'Draft';
const GOVERNANCE_STATUS_UNDER_REVIEW = 'Under_Review';
const GOVERNANCE_STATUS_CERTIFIED = 'Certified';

const DOMAIN_FIELD_DRAFT = 'draft' as const;
const DOMAIN_FIELD_REVIEW = 'review' as const;
const DOMAIN_FIELD_CERTIFIED = 'certified' as const;

function buildAggInput(status: string, dashboardFilters: FacetFilterInput[]) {
    return {
        query: '*',
        facets: [DOMAINS_FILTER_NAME],
        types: COMMON_ASSET_ENTITY_TYPES,
        orFilters: mergeDashboardFilters(dashboardFilters, [
            { and: [{ field: GOVERNANCE_STATUS_FILTER, values: [status] }] },
        ]),
    };
}

interface DomainGovernanceData {
    name: string;
    draft: number;
    review: number;
    certified: number;
    total: number;
}

export const GovernanceStatusPerDomainWidget = () => {
    const { t } = useTranslation();
    const dashboardFilters = useDashboardFilters();

    const { data, loading } = useAggregateGovernanceStatusByDomainQuery({
        variables: {
            draftInput: buildAggInput(GOVERNANCE_STATUS_DRAFT, dashboardFilters),
            reviewInput: buildAggInput(GOVERNANCE_STATUS_UNDER_REVIEW, dashboardFilters),
            certifiedInput: buildAggInput(GOVERNANCE_STATUS_CERTIFIED, dashboardFilters),
        },
        fetchPolicy: 'no-cache',
    });

    const { categories, series } = useMemo<{ categories: string[]; series: ColumnChartSeriesItem[] }>(() => {
        const draftAggs = getFacetAggregations(data?.draftByDomain?.facets, DOMAINS_FILTER_NAME);
        const reviewAggs = getFacetAggregations(data?.reviewByDomain?.facets, DOMAINS_FILTER_NAME);
        const certifiedAggs = getFacetAggregations(data?.certifiedByDomain?.facets, DOMAINS_FILTER_NAME);

        const domainMap = new Map<string, DomainGovernanceData>();

        const processAggs = (
            aggs: typeof draftAggs,
            field: typeof DOMAIN_FIELD_DRAFT | typeof DOMAIN_FIELD_REVIEW | typeof DOMAIN_FIELD_CERTIFIED,
        ) => {
            aggs.forEach((agg) => {
                const key = agg.value;
                const domainName = getAggEntityName(agg);

                if (!domainMap.has(key)) {
                    domainMap.set(key, {
                        name: domainName || key.split(':').pop() || 'Unknown',
                        draft: 0,
                        review: 0,
                        certified: 0,
                        total: 0,
                    });
                }

                const entry = domainMap.get(key)!;
                if (domainName && entry.name === key.split(':').pop()) {
                    entry.name = domainName;
                }
                entry[field] = agg.count;
                entry.total = entry.draft + entry.review + entry.certified;
            });
        };

        processAggs(draftAggs, DOMAIN_FIELD_DRAFT);
        processAggs(reviewAggs, DOMAIN_FIELD_REVIEW);
        processAggs(certifiedAggs, DOMAIN_FIELD_CERTIFIED);

        const topDomains = Array.from(domainMap.values())
            .filter((d) => d.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, TOP_DOMAINS_LIMIT);

        if (topDomains.length === 0) {
            return { categories: [], series: [] };
        }

        const { governanceStatusColorMap } = colorUtilService;

        const builtSeries: ColumnChartSeriesItem[] = [
            {
                name: t('Draft'),
                color: governanceStatusColorMap.Draft,
                data: topDomains.map((d) => d.draft),
            },
            {
                name: t('Review'),
                color: governanceStatusColorMap.Review,
                data: topDomains.map((d) => d.review),
            },
            {
                name: t('Certified'),
                color: governanceStatusColorMap.Certified,
                data: topDomains.map((d) => d.certified),
            },
        ];

        return { categories: topDomains.map((d) => d.name), series: builtSeries };
    }, [data, t]);

    const hasData = series.length > 0 && categories.length > 0;

    return (
        <ChartWidgetCard title={t('Governance Status per Domain')} loading={loading} isEmpty={!hasData}>
            <ColumnChart showLegend height={310} series={series} categories={categories} />
        </ChartWidgetCard>
    );
};
